import { takeEvery, takeLatest, put, select } from "redux-saga/effects";
import {
  getCustomerRecord,
  getCustomerDetailsUsingMobile,
  getCustomerSummary,
  authenticateCustomerData,
  saveCustomerInfo,
  getDetailedCustomerInfo,
} from "../../../slices/CustomerSlice";
import api from "../../../../utils/apiService";
import { ENDPOINT } from "../../../../config/endpoints";
import { prepareCommonCustomerData } from "../../../../helpers/customerHelpers";
import { METHODS } from "../../../../data/constants";
import { selectActiveLoyaltyProvider } from "../../../selectors/loyaltySelectors";
import {
  authenticateCustomer,
  fetchCustomerSummary,
  getCustomerInfo,
  getDetailedCustomer,
} from "./shared";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/pos/customer` — `saveCustomerData` plus the watcher list.
 *
 * ⚠️ **Every worker `root` wires is IMPORTED as well as re-exported** (`export * from "./shared"`):
 * a re-export creates no local binding (ledger rule 5 / hazard 30).
 *
 * **`getCustomerInfo`, `getDetailedCustomer` and `selectChannelDefId` moved to `./shared` on
 * 2026-09-05, unchanged**, when the other platform took the same consent flow and therefore
 * needed these bodies rather than its own. Nothing here behaves differently.
 *
 * ⚠️ `saveCustomerData` stays: both apps shape the body with the shared
 * `prepareCommonCustomerData`, but only this one has the consent handling and
 * `skipGlobalLoader`, and the action payloads differ.
 *
 */

/**
 * Save the customer record.
 *
 * 🔴🔴 **THE CONSENT BLOCK BELOW IS DEAD CODE. DO NOT COPY IT TO ANOTHER PLATFORM.**
 *
 * `payload` is built with `customerConsentProvided` / `providerConsents`, mutated, and then
 * **never used**: the request sends `preparedCustomerData` (see `data:` below, where `payload`
 * is commented out). Consent reaches the backend ONLY through `customer/consent-otp/verify`.
 *
 * It reads like the consent-save path and is not one. Porting this worker to mobile "for the
 * consent handling" would copy the dead block.
 *
 * Switching it on is a one-line change (`data: payload`) **plus backend confirmation** — see the
 * known gap in `docs/flows/LOYALTY.md`. Left wired-but-off deliberately; the marker is the
 * commented-out `payload` on the `data:` line.
 */
function* saveCustomerData(action: any): any {
  // `onFailure` is given only by Reservations v2's customer picker (RESERVATIONS_V2_PLAN.md decision 105): it must hear
  // that a new customer could not be saved, so its Create form stops waiting. No other caller passes it.
  const {
    customerData,
    providerConsents,
    customerConsentProvided,
    callback,
    onSaveSuccess,
    onSaveError,
    onFailure,
  } = action.payload || {};
  const preparedCustomerData = prepareCommonCustomerData(customerData);
  const {redemptionScheme, membershipId, membershipName, membershipStart, membershipEnd} = customerData || {};
  // ⚠️ DEAD from here to the `try` — see the note above.
  const payload: any = {
    ...preparedCustomerData,
    customerConsentProvided,
    providerConsents,
  }
  if (customerConsentProvided) {
    const externalLoyaltyProvider = yield select(selectActiveLoyaltyProvider);
    const providerConsentData: any = {
      ...(providerConsents || {})
    };
    if (externalLoyaltyProvider?.metaId) {
      providerConsentData[externalLoyaltyProvider.metaId] = true;
    }
    payload.providerConsents = providerConsentData;
  }

  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.POS.saveCustomer(),
      data: preparedCustomerData, // payload,
      skipGlobalLoader: action.payload?.skipGlobalLoader
    } as any);
    if (response && !response.code) {
      yield put(getCustomerRecord({...response, redemptionScheme, membershipId, membershipName, membershipStart, membershipEnd}));
      onSaveSuccess?.(response);
      if (callback && !customerData?.customerId) {
        callback(response)
      }
    } else {
      onSaveError?.();
      onFailure?.();
    }
  }
  catch (error) {
    onSaveError?.();
    console.log('error', error);
    onFailure?.();
  }
}

export default function* root(): any {
  yield takeLatest(getCustomerDetailsUsingMobile.type, getCustomerInfo);
  yield takeEvery(getCustomerSummary.type, fetchCustomerSummary);
  yield takeEvery(authenticateCustomerData.type, authenticateCustomer);
  yield takeEvery(saveCustomerInfo.type, saveCustomerData);
  yield takeLatest(getDetailedCustomerInfo.type, getDetailedCustomer);
}
